import { EventHandler, WithDelegateTarget } from "../../utils/event-handler";
import { throttle } from "../../utils";
import { EVENT_CLICK, EVENT_RESIZE } from "../../utils/events";

import "./gallery-grid.scss";

export class GalleryGrid {
    #SELECTOR_LIST = '.gallery-grid';
    #SELECTOR_WRAPPER = `${this.#SELECTOR_LIST}__wrapper`;
    #SELECTOR_ITEM = `${this.#SELECTOR_LIST}__item`;
    #SELECTOR_BTN = `${this.#SELECTOR_LIST}__btn[aria-controls]`;

    #CLASS_NAME_LIST_OPEN = 'open';
    #CLASS_NAME_LIST_NOT_OVERFLOWING = 'not-overflowing';

    constructor() {
        this.#initElements();
        this.#initEvents();
    }

    #initElements(): void {
        document.querySelectorAll<HTMLElement>(`${this.#SELECTOR_LIST} ${this.#SELECTOR_BTN}`).forEach((btn) => {
            const list = this.#getList(btn);

            this.#setAriaExpanded(btn, this.#isOpen(list));

            this.#checkListOverflow(list);
        });
    }

    #initEvents(): void {
        EventHandler.on(document, EVENT_CLICK, `${this.#SELECTOR_LIST} ${this.#SELECTOR_BTN}`, (event: WithDelegateTarget<MouseEvent>) => {
            this.open(this.#getList(<HTMLElement>event.delegateTarget));
        });

        EventHandler.on(window, EVENT_RESIZE, throttle(() => {
            document.querySelectorAll<HTMLElement>(`${this.#SELECTOR_LIST} ${this.#SELECTOR_BTN}`).forEach((btn) => {
                this.#checkListOverflow(this.#getList(btn));
            });
        }, 100));
    }

    #getList(btn: HTMLElement): HTMLElement {
        return document.querySelector(`${this.#SELECTOR_LIST}#${btn.getAttribute('aria-controls')}`);
    }

    #getBtn(list: HTMLElement): HTMLElement {
        return document.querySelector(`${this.#SELECTOR_BTN}[aria-controls="${list.id}"]`);
    }

    #isOpen(list: HTMLElement): boolean {
        return list.classList.contains(this.#CLASS_NAME_LIST_OPEN);
    }

    #setAriaExpanded(btn: HTMLElement, isOpen: boolean): void {
        btn.setAttribute('aria-expanded', String(isOpen));
    }

    open(list: HTMLElement): void {
        if (this.#isOpen(list)) {
            return;
        }

        const btn = this.#getBtn(list);

        Array.from(list.querySelectorAll<HTMLImageElement>(`${this.#SELECTOR_ITEM} [data-src]`))
            .forEach((img) => {
                img.src = img.dataset.src;
                delete img.dataset.src;
            });

        list.classList.add(this.#CLASS_NAME_LIST_OPEN);

        this.#setAriaExpanded(btn, true);
    }

    #checkListOverflow(list: HTMLElement): void {
        if (this.#isOpen(list)) {
            return;
        }

        const wrapper = list.querySelector<HTMLElement>(this.#SELECTOR_WRAPPER);
        const style = window.getComputedStyle(wrapper);
        const rows = style.getPropertyValue('grid-template-rows').split(' ').length;

        list.classList.toggle(this.#CLASS_NAME_LIST_NOT_OVERFLOWING, rows === 1);
    }
}
